import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SwipeIcon from "../assets/helperIcons/SwipeIcon";

const CustomDropdown = ({
  products,
  handleNewItemProductSelect,
  formatCurrency,
  selected,
  disabled,
  zIndex = 20000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const handleSelect = (productId) => {
    handleNewItemProductSelect(productId);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // The menu is portalled to the viewport, so its coordinates must come
  // directly from getBoundingClientRect (without document scroll offsets).
  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight || Math.min(320, window.innerHeight - 16);
      const openUpward = window.innerHeight - rect.bottom < menuHeight && rect.top > window.innerHeight - rect.bottom;
      const width = Math.max(rect.width, 300);
      setDropdownPosition({
        top: openUpward ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !rootRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedProduct = products.find((p) => p._id === selected);

  return (
    <div className="relative w-full text-sm" ref={rootRef}>
      <div className="flex items-center gap-2" ref={buttonRef}>
        <SwipeIcon />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-2 py-1 text-left flex justify-between items-center focus:outline-none focus:ring-0 focus:ring-purple-500 rounded ${
            disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          <span>
            {disabled
              ? "Package Item"
              : selectedProduct
              ? `${selectedProduct.name}`
              : "Select Product"}
          </span>

          {!disabled && (
            <svg
              className={`w-4 h-4 ml-2 transition-transform duration-200 ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>
      </div>

      {isOpen && !disabled && createPortal(
        <div 
          ref={menuRef}
          className="fixed bg-white border border-gray-300 rounded shadow-lg max-h-72 overflow-y-auto"
          style={{ 
            zIndex,
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          {/* Search input */}
          <div className="p-2 relative">
            <svg
              className="w-4 h-4 text-gray-400 absolute top-1/2 left-3 transform -translate-y-1/2 pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#F7F7F8] p-[8px] pl-7 text-xs rounded focus:outline-none focus:ring-0 focus:ring-purple-500"
            />
          </div>

          {/* Product List */}
          <ul className="max-h-72 overflow-y-auto">
            {filteredProducts.map((product) => (
              <li
                key={product._id}
                onClick={() => handleSelect(product._id)}
                className="cursor-pointer px-3 py-2 hover:bg-gray-100 text-gray-800 flex justify-between items-center"
              >
                <span className="truncate">{product.name}</span>
                <span className="ml-2 text-gray-500 text-xs">
                  {formatCurrency(product.price)}
                </span>
              </li>
            ))}

            {/* + Add New */}
            <li
              onClick={() => handleSelect("add_new")}
              className="cursor-pointer border-t border-gray-200 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              + Add New Product
            </li>
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomDropdown;
